import React, { type ReactNode } from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { taskdetailscardContract } from './taskdetailscard.contract'

const VARIANTS = taskdetailscardContract.props.variant.values

export type TaskDetailsCardVariant = (typeof VARIANTS)[number]

const SHIFT_NOTES = taskdetailscardContract.variants['variant=shift-notes'].partTextStyles

export const TASK_DETAILS_CARD_TEXT = variantForStyleId(SHIFT_NOTES['Thu, 09.07.2026'])

export type TaskDetailsCardProps = {
    stripe?: ReactNode
    title?: ReactNode
    badge?: ReactNode
    datetime?: ReactNode
    meta?: ReactNode
    actions?: ReactNode
    children: ReactNode
    variant?: TaskDetailsCardVariant
    className?: string
    testID?: string
}

type ContractCell = {
    container: string
    label: string
    icon: string
    parts: Record<string, string>
    partTextStyles: Record<string, string>
}

const variantFor = (variant: TaskDetailsCardVariant): ContractCell => {
    const key = `variant=${variant}`
    const found = (taskdetailscardContract.variants as Record<string, ContractCell>)[key]
    if (!found) {
        throw new Error(`aos-ds TaskDetailsCard: the Figma contract has no "${key}"`)
    }
    return found
}

const mutedKeyOf = (cell: ContractCell): string | undefined =>
    Object.keys(cell.partTextStyles).find(key =>
        (cell.parts[key] ?? '').includes('text-content-muted'),
    )

const BODY_KEY = 'Ground handling'

export const taskStatusLabelClasses = (variant: TaskDetailsCardVariant): string => {
    const found = variantFor(variant).parts.TaskStatusLabel
    if (!found) {
        throw new Error(`aos-ds TaskDetailsCard: "variant=${variant}" draws no TaskStatusLabel`)
    }
    return found
}

export const TaskDetailsCard = ({
    stripe,
    title,
    badge,
    datetime,
    meta,
    actions,
    children,
    variant = 'shift-notes',
    className,
    testID,
}: TaskDetailsCardProps) => {
    const cell = variantFor(variant)
    const hasFooter = !!cell.parts.footer
    const metaKey = mutedKeyOf(cell)
    const metaNode =
        meta && metaKey ? (
            <Typography
                variant={variantForStyleId(cell.partTextStyles[metaKey])}
                className={cell.parts[metaKey]}
                testID='task-details-card-meta'
            >
                {meta}
            </Typography>
        ) : null
    /**
     * `firefighter-rescue-unit` names its two dated blocks `datetime-from` and
     * `datetime-to`; FOD names none, because its footer draws the date and the
     * time as a single text node and has no row to describe. A caller that
     * hands over several nodes still needs them spaced, so the fallback is the
     * row the file does describe — shift-notes' `datetime`. Without it the FOD
     * footer rendered the date, the clock and the time flush against each other
     * with a gap of exactly zero.
     */
    const datetimeClasses =
        cell.parts.datetime ??
        cell.parts['datetime-from'] ??
        taskdetailscardContract.variants['variant=shift-notes'].parts.datetime
    const datetimeNode = datetime ? (
        <View className={twMerge('flex-row', datetimeClasses)}>{datetime}</View>
    ) : null
    return (
        <View
            className={twMerge('flex-row', cell.container, className)}
            testID={testID ?? `task-details-card-${variant}`}
        >
            {stripe ? <View className='h-auto self-stretch'>{stripe}</View> : null}
            <View className={twMerge('flex-1', cell.parts.content)}>
                <View className={twMerge('w-full flex-row', cell.parts.header)}>
                    <View className={twMerge('flex-1 flex-row', cell.parts['header-content'])}>
                        {title ? (
                            <View
                                className={twMerge('flex-col', cell.parts['title-block'])}
                                testID='task-details-card-title'
                            >
                                {title}
                            </View>
                        ) : null}
                        {badge ? (
                            <View
                                className={twMerge('flex-row', cell.parts.TaskStatusLabel)}
                                testID='task-details-card-badge'
                            >
                                {badge}
                            </View>
                        ) : null}
                        {hasFooter ? null : datetimeNode}
                        {hasFooter ? null : metaNode}
                        {actions ? (
                            <View
                                className={twMerge(
                                    'flex-1 flex-row justify-end',
                                    cell.parts.actions,
                                )}
                                testID='task-details-card-actions'
                            >
                                {actions}
                            </View>
                        ) : null}
                    </View>
                </View>
                <View
                    className={twMerge(
                        'w-full flex-row',
                        cell.parts.body,
                        typeof children === 'string' ? undefined : 'h-auto items-start',
                    )}
                >
                    {typeof children === 'string' ? (
                        <Typography
                            variant={variantForStyleId(cell.partTextStyles[BODY_KEY])}
                            className={twMerge('whitespace-pre-line', cell.parts[BODY_KEY])}
                            testID='task-details-card-body'
                        >
                            {children}
                        </Typography>
                    ) : (
                        children
                    )}
                </View>
                {hasFooter ? (
                    <View
                        className={twMerge('w-full flex-row', cell.parts.footer)}
                        testID='task-details-card-footer'
                    >
                        <View className={twMerge('flex-1 flex-row', cell.parts['footer-content'])}>
                            {datetimeNode}
                            {metaNode}
                        </View>
                    </View>
                ) : null}
            </View>
        </View>
    )
}

TaskDetailsCard.displayName = 'TaskDetailsCard'

export { VARIANTS as TASK_DETAILS_CARD_VARIANTS }
